import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { authParams, getBase64Hash } from 'components/helpers/base64AuthParams';
import { currencyOpts } from 'components/helpers/currencyOptions';
import getLanguage from 'components/helpers/getLanguage';
import translationKeys from 'translations/translationKeys';
import baseUrls from '../../config';

const apiBaseUrl = baseUrls.BASE_URL_API;
const apiVersion2 = baseUrls.API_VERSION2;
const client = axios.create({
  baseURL: apiBaseUrl,
});
const { msg229 } = translationKeys;
export const getCurrencyConfigs = createAsyncThunk(
  'configs/getCurrencyConfigs',
  async (thunkAPI) => {
    const lang = getLanguage();
    const api = `/system/configs/currency?lang=${lang}`;
    const signatureParam = `/system/configs/currencylang=${lang}`;
    const url = `/${apiVersion2}${api}`;
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          'Content-Signature': `${authParams.username}:${getBase64Hash(`${authParams.username}/${apiVersion2}${signatureParam}`)}`,
        },
      };

      if (baseUrls.PARTNER_SLUG) {
        config.headers['X-Partner-Slug'] = baseUrls.PARTNER_SLUG;
      }

      const resp = await client.get(url, config);
      return resp.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const getConversionRate = createAsyncThunk(
  'configs/getConversionRate',
  async ({ sourceCurrency, targetCurrency }, thunkAPI) => {
    const api = `/currency-conversion/rate?sourceCurrency=${sourceCurrency}&targetCurrency=${targetCurrency}`;
    const signatureParam = `/currency-conversion/ratesourceCurrency=${sourceCurrency}&targetCurrency=${targetCurrency}`;
    const url = `/${apiVersion2}${api}`;
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          'Content-Signature': `${authParams.username}:${getBase64Hash(`${authParams.username}/${apiVersion2}${signatureParam}`)}`,
        },
      };

      if (baseUrls.PARTNER_SLUG) {
        config.headers['X-Partner-Slug'] = baseUrls.PARTNER_SLUG;
      }

      const resp = await client.get(url, config);
      const { data } = resp;

      // ── Validate response shape ──────────────────────────
      const isValid = (
        data
        && typeof data === 'object'
        && typeof data.conversionRate === 'number'
        && Number.isFinite(data.conversionRate)
        && data.conversionRate > 0
        && typeof data.ttlMs === 'number'
        && data.ttlMs > 0
      );

      if (!isValid) {
        return thunkAPI.rejectWithValue({
          targetCurrency,
          error: `Invalid conversion rate response: ${JSON.stringify(data)}`,
        });
      }

      return {
        sourceCurrency,
        targetCurrency,
        conversionRate: data.conversionRate,
        ttlMs: data.ttlMs,
        fetchedAt: Date.now(),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({ targetCurrency, error: error.message });
    }
  },
);

const initialState = {
  currencyConfigs: currencyOpts,
  secondaryCurrencyConfigs: [],
  currencyConfigsSuccess: false,
  isLoading: false,
  error: '',

  // Conversion rate state
  conversionRate: null, // string, e.g. "26.7713"
  conversionTargetCurrency: null,
  conversionExpiresAt: null, // Date.now() + ttlMs
  conversionLoading: false,
  conversionError: '',
};

const currencyConfigsSlice = createSlice({
  name: 'currencyConfigs',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(getConversionRate.pending, (state) => ({
        ...state,
        conversionLoading: true,
        conversionError: '',
      }))
      .addCase(getConversionRate.fulfilled, (state, { payload }) => ({
        ...state,
        conversionLoading: false,
        conversionRate: payload.conversionRate,
        conversionTargetCurrency: payload.targetCurrency,
        conversionExpiresAt: payload.fetchedAt + payload.ttlMs,
      }))
      .addCase(getConversionRate.rejected, (state, { payload }) => ({
        ...state,
        conversionLoading: false,
        conversionError: msg229,
        conversionTargetCurrency: payload?.targetCurrency ?? state.conversionTargetCurrency,
      }))
      .addCase(getCurrencyConfigs.pending, (state) => ({
        ...state,
        isLoading: true,
        currencyConfigsSuccess: false,
      }))
      .addCase(getCurrencyConfigs.fulfilled, (state, { payload }) => {
        let currencyConfigs = currencyOpts;
        let secondaryCurrencyConfigs = [];

        if (payload?.msg === 'success') {
          const json = JSON.parse(payload.data);
          if (Array.isArray(json)) {
            currencyConfigs = json.find((c) => c.bDefault) || json[0];
            secondaryCurrencyConfigs = json.filter((c) => c !== currencyConfigs);
          } else if (json && Object.keys(json).length) {
            currencyConfigs = json;
          }
        }

        return {
          ...state,
          currencyConfigs,
          secondaryCurrencyConfigs,
          isLoading: false,
          currencyConfigsSuccess: true,
        };
      })

      .addCase(getCurrencyConfigs.rejected, (state) => ({
        ...state,
        isLoading: false,
        currencyConfigsSuccess: false,
        error: 'Something went wrong',
      }));
  },
});

export const selectConversionRate = (state) => state.currencyConfigs.conversionRate;
export const selectConversionExpiresAt = (state) => state.currencyConfigs.conversionExpiresAt;
export const selectConversionLoading = (state) => state.currencyConfigs.conversionLoading;
export const selectConversionError = (state) => state.currencyConfigs.conversionError;
export const selectConversionTargetCurrency = (state) => state.currencyConfigs
  .conversionTargetCurrency;
export const selectSecondaryCurrencyConfigs = (state) => state
  .currencyConfigs.secondaryCurrencyConfigs;

// True when the rate is missing, expired, or for the wrong target currency
export const selectConversionRateStale = (targetCurrency) => (state) => {
  const { conversionRate, conversionExpiresAt, conversionTargetCurrency } = state.currencyConfigs;
  if (!conversionRate || conversionTargetCurrency !== targetCurrency) return true;
  return Date.now() >= conversionExpiresAt;
};

export default currencyConfigsSlice.reducer;
